import { BookingRequestPayload, BookingResponse, BookingSubmissionResult } from '../types/booking'

/**
 * Direct integration with the deployed Google Apps Script web app (doPost).
 *
 * Why text/plain: Apps Script web apps cannot answer CORS preflight (OPTIONS) requests.
 * A POST with Content-Type "application/json" or any custom header triggers a preflight and
 * fails in the browser. A "simple" request (text/plain body, no custom headers) skips the
 * preflight; Apps Script follows a 302 to googleusercontent.com, whose JSON response carries
 * Access-Control-Allow-Origin: *, so the browser CAN read the body (including the payment URL).
 * doPost must read the body via JSON.parse(e.postData.contents).
 */
export const APPS_SCRIPT_URL =
  'https://script.google.com/macros/s/AKfycbzLmOaQGR_RcS1J73AhXh-C3ZSWUNOhw779cpVNtzcjzx0Q2g-945DSwBpHbKX8Q6HJ7w/exec'

/** Set to false to pause online bookings without touching the form. */
export const SUBMISSION_ENABLED = true

/** Response keys checked (in order) for the Paystack authorization URL returned by doPost. */
const PAYMENT_URL_KEYS = ['paymentUrl', 'authorizationUrl', 'authorization_url'] as const

/** Only ever redirect guests to Paystack-hosted checkout pages. */
const ALLOWED_PAYMENT_HOSTS = ['checkout.paystack.com', 'paystack.com', 'paystack.co']

export type IntegrationStatus = 'disabled' | 'ready'

export function getIntegrationStatus(): IntegrationStatus {
  return SUBMISSION_ENABLED ? 'ready' : 'disabled'
}

export class BookingApiError extends Error {
  constructor(
    message: string,
    public readonly kind: 'disabled' | 'network' | 'invalid-response',
  ) {
    super(message)
  }
}

function toNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) return Number(value)
  return undefined
}

function toText(value: unknown): string | undefined {
  if (typeof value === 'number') return String(value)
  return typeof value === 'string' && value.trim() !== '' ? value : undefined
}

function safePaymentUrl(raw: Record<string, unknown>): string | undefined {
  for (const key of PAYMENT_URL_KEYS) {
    const value = toText(raw[key])
    if (!value) continue
    try {
      const url = new URL(value)
      const hostOk = ALLOWED_PAYMENT_HOSTS.some((host) => url.hostname === host || url.hostname.endsWith(`.${host}`))
      if (url.protocol === 'https:' && hostOk) return url.toString()
    } catch {
      /* not a valid URL — ignore */
    }
  }
  return undefined
}

function normalise(data: unknown): BookingResponse {
  const raw = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>
  return {
    ok: raw.ok === true || raw.ok === 'true',
    bookingId: toText(raw.bookingId),
    bookingStatus: toText(raw.bookingStatus),
    paymentStatus: toText(raw.paymentStatus),
    message: toText(raw.message) ?? toText(raw.error),
    currency: toText(raw.currency),
    baseAmount: toNumber(raw.baseAmount),
    earlyFee: toNumber(raw.earlyFee),
    lateFee: toNumber(raw.lateFee),
    cautionFee: toNumber(raw.cautionFee),
    totalAmount: toNumber(raw.totalAmount),
    paymentUrl: safePaymentUrl(raw),
    reference: toText(raw.reference) ?? toText(raw.paymentReference),
  }
}

/**
 * Sends the booking to Apps Script and returns the backend's actual response — including
 * unsuccessful ones, so the guest sees the real reason. Never fabricates a result.
 */
export async function submitBookingRequest(payload: BookingRequestPayload): Promise<BookingSubmissionResult> {
  if (!SUBMISSION_ENABLED) {
    throw new BookingApiError('Online booking is paused. Please contact us to book.', 'disabled')
  }

  // Debug only: field names + booking type. No personal data or secrets are logged.
  console.info('[booking] submitting to Apps Script', {
    fields: Object.keys(payload),
    bookingType: payload.bookingType,
    propertyId: payload.propertyId,
  })

  let res: Response
  try {
    res = await fetch(APPS_SCRIPT_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
      redirect: 'follow',
    })
  } catch {
    // Also what the browser reports when doPost throws: Apps Script then returns an HTML
    // error page without CORS headers, which surfaces here as a network/CORS failure.
    throw new BookingApiError(
      'We couldn’t reach the booking system. Please check your connection and try again — nothing has been booked or charged.',
      'network',
    )
  }

  const text = await res.text()
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    throw new BookingApiError(
      `The booking system returned an unexpected response (HTTP ${res.status}). Nothing has been booked or charged — please try again or contact us.`,
      'invalid-response',
    )
  }

  const response = normalise(raw)
  if (!response.ok) {
    console.warn('[booking] backend rejected request', {
      httpStatus: res.status,
      message: response.message,
      bookingType: payload.bookingType,
      fields: Object.keys(payload),
    })
  }
  return { response, raw, httpStatus: res.status }
}
